import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { Trophy, Play, Loader2, Code2, Zap, Clock } from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

/**
 * StudentConquest — weekly code-conquest UI wired to
 * ``/api/conquest/active`` (fetch round + problems, plus my_best_score),
 * ``/api/conquest/{round_id}/submit`` (submit code, get score),
 * ``/api/conquest/{round_id}/leaderboard`` (top-20 for the round).
 *
 * A single active round is displayed at a time; each of its problems
 * gets its own editor + submit flow. Scores are stored per submission
 * so learners can iterate mid-round; leaderboards use the max score
 * per (user, problem) pair (aggregated backend-side).
 */
export default function StudentConquest() {
  usePageTitle("Code Conquest");
  const [round, setRound] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/conquest/active");
      setRound(r.data);
      try {
        const lb = await api.get(`/conquest/${r.data.id}/leaderboard?limit=10`);
        setLeaders(lb.data);
      } catch {
        // Leaderboard is a nice-to-have; ignore if endpoint hiccups.
      }
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="student-conquest">
      <div className="rounded-3xl p-8 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #006CFF 0%, #00B8D4 100%)" }}>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/90 inline-flex items-center gap-1">
          <Trophy size={12} /> Weekly conquest
        </span>
        <h1 className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">
          Solve. Submit. Climb the leaderboard.
        </h1>
        <p className="text-white/85 text-sm mt-2 max-w-2xl">
          Fresh coding rounds every Monday. Auto-graded against hidden test cases.
          Your best score across the week counts toward this round's rank.
        </p>
        {round && (
          <div className="flex flex-wrap gap-6 mt-6 text-sm">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Round</div>
              <div className="font-display font-black text-lg" data-testid="conquest-title">{round.title}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Ends</div>
              <div className="font-mono text-sm mt-0.5" data-testid="conquest-end">
                {round.end_at?.slice(0, 10)}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Your total</div>
              <div className="font-display font-black text-lg" data-testid="conquest-my-score">
                {round.my_best_score ?? 0}
              </div>
            </div>
          </div>
        )}
      </div>

      {err && <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3" data-testid="conquest-error">{err}</div>}
      {loading && (
        <div className="card card-pad text-muted flex items-center gap-2" data-testid="conquest-loading">
          <Loader2 size={16} className="animate-spin"/> Loading active round…
        </div>
      )}

      {!loading && round?.problems?.map((p, i) => (
        <ProblemCard
          key={p.id}
          number={i + 1}
          problem={p}
          roundId={round.id}
          onSubmitted={load}
        />
      ))}

      {leaders?.length > 0 && (
        <div className="card card-pad" data-testid="conquest-leaderboard">
          <div className="flex items-center gap-1.5 mb-3">
            <Trophy size={14} className="text-signal"/>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider">
              This round · Top {leaders.length}
            </h3>
          </div>
          <div className="grid gap-1">
            {leaders.map((r) => (
              <div key={r.user_id}
                className="flex items-center gap-3 py-2 border-b last:border-0"
                style={{ borderColor: "var(--rule)" }}
                data-testid={`conquest-rank-${r.rank}`}
              >
                <div className="w-8 text-center font-display font-black text-sm text-muted">#{r.rank}</div>
                <div className="flex-1 font-semibold text-sm truncate">{r.name}</div>
                <div className="text-signal font-black tabular-nums">{r.score}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProblemCard({ number, problem, roundId, onSubmitted }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastScore, setLastScore] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!code.trim()) { setErr("Write some code first."); return; }
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/conquest/${roundId}/submit`, {
        problem_id: problem.id,
        code,
        language: problem.language || "python",
      });
      setLastScore({ score: r.data.score, max: r.data.max_score });
      onSubmitted?.();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card-pad" data-testid={`conquest-problem-${problem.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-full bg-signal text-white font-black text-sm grid place-items-center">
          {number}
        </span>
        <h3 className="font-display font-bold text-xl">{problem.title}</h3>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-signal">
          <Code2 size={11}/> {problem.language || "python"}
        </span>
      </div>
      <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 bg-paper rounded-xl p-4 mb-3">
        {problem.prompt}
      </pre>
      {problem.sample_io?.length > 0 && (
        <div className="grid gap-2 mb-3">
          {problem.sample_io.map((tc, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 text-xs font-mono">
              <div className="rounded-lg bg-slate-50 p-2">
                <div className="text-[10px] uppercase font-bold text-muted mb-1">Input</div>
                <div>{tc.input}</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-2">
                <div className="text-[10px] uppercase font-bold text-muted mb-1">Expected</div>
                <div className="whitespace-pre-wrap">{tc.expected}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <textarea
        className="input font-mono text-sm min-h-[180px] w-full"
        placeholder={`# Write your ${problem.language || "python"} solution here`}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        data-testid={`conquest-code-${problem.id}`}
      />
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button
          type="button"
          className="btn-primary"
          onClick={submit}
          disabled={busy || !code.trim()}
          data-testid={`conquest-submit-${problem.id}`}
        >
          {busy ? <><Loader2 size={14} className="animate-spin"/> Grading…</> : <><Play size={14}/> Submit</>}
        </button>
        {lastScore && (
          <div
            className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
              lastScore.score >= lastScore.max ? "text-emerald-700"
              : lastScore.score > 0 ? "text-amber-700"
              : "text-red-700"
            }`}
            data-testid={`conquest-score-${problem.id}`}
          >
            <Zap size={14}/> Score: {lastScore.score} / {lastScore.max}
          </div>
        )}
        <span className="text-xs text-muted inline-flex items-center gap-1 ml-auto">
          <Clock size={11}/> Up to 6s runtime per test
        </span>
      </div>
      {err && <p className="text-sm text-red-600 mt-2" data-testid={`conquest-error-${problem.id}`}>{err}</p>}
    </div>
  );
}
